import React, { useState } from "react";
import styles from "./koifishcafe.module.css";
import TextField from "@mui/material/TextField";
import {
  InputLabel,
  Select,
  FormControl,
  MenuItem,
  Button,
  FormHelperText,
  Modal,
  Box,
} from "@mui/material";
import Head from "next/head";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PlaceIcon from "@mui/icons-material/Place";
import LanguageIcon from "@mui/icons-material/Language";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import YouTubeIcon from "@mui/icons-material/YouTube";
import SchoolIcon from "@mui/icons-material/School";

export default function Index() {
  const [user, setUser] = useState({});
  const [error, setError] = useState({});
  const [successOpen, setSuccessOpen] = useState(false);
  const closeSuccess = () => setSuccessOpen(false);
  const [wheelOpen, setWheelOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleInput = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setError({ ...error, [name]: undefined });
    setUser({ ...user, [name]: value });
  };

  // <--------------------------- Phone Formatting --------------------------->
  const phoneFormatting = (event) => {
    if (event.target.value.split("").length <= 18) {
      const formatted = event.target.value.replace(/[^\d+\-()\s]/g, "");
      const name = event.target.name;
      setUser({ ...user, [name]: formatted });
      setError({ ...error, [name]: undefined });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    let errorObject = {};

    if (!user?.first_name) errorObject.first_name = "First Name is Required";
    if (!user?.last_name) errorObject.last_name = "Last Name is Required";
    if (!user?.email) errorObject.email = "Email is Required";
    else if (!/\S+@\S+\.\S+/.test(user.email))
      errorObject.email = "* Invalid email format";
    if (!user?.contact_number)
      errorObject.contact_number = "Telephone Number is Required";
    if (!user?.lives_in_danang)
      errorObject.lives_in_danang = "Please select an option";
    if (!user?.experience)
      errorObject.experience = "Please select your experience level";
    if (!user?.age) errorObject.age = "Age is Required";
    if (!user?.gender) errorObject.gender = "Gender is Required";
    if (!user?.believe_in_astrology)
      errorObject.believe_in_astrology = "Please select an option";

    if (Object.keys(errorObject).length > 0) {
      setError(errorObject);
      return;
    }

    setError({});
    setSubmitError("");

    // POST to the Koi Fish Cafe meetup registration endpoint. Field values already
    // match the API enums (see the *Array definitions below).
    const payload = {
      firstName: user.first_name.trim(),
      lastName: user.last_name.trim(),
      email: user.email.trim(),
      phone: user.contact_number.trim(),
      livesInDaNang: user.lives_in_danang,
      astrologyExperience: user.experience,
      age: Number(user.age),
      gender: user.gender,
      believesInAstrology: user.believe_in_astrology,
    };

    try {
      setSubmitting(true);
      const res = await fetch(
        "https://api.divineinfinitebeing.com/event/meetup-register-koifishcafe",
        {
          method: "POST",
          headers: {
            accept: "*/*",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      // The API returns 200 with { success:false } for handled cases
      // (e.g. duplicate email), so branch on the body, not just res.ok.
      const data = await res.json().catch(() => null);

      if (res.ok && data?.success) {
        setSuccessOpen(true);
        setUser({});
      } else {
        setSubmitError(
          data?.message ||
            "Something went wrong while reserving your seat. Please try again."
        );
      }
    } catch (err) {
      setSubmitError(
        "Network error — please check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // `val`s below are the exact enum values the registration API accepts.
  const YesNoArray = [
    { name: "Yes", val: "Yes" },
    { name: "No", val: "No" },
  ];

  const ExperienceArray = [
    { name: "Beginner", val: "Beginner" },
    { name: "Some Experience", val: "Some Experience" },
    { name: "Advanced", val: "Advanced" },
  ];

  const GenderArray = [
    { name: "Male", val: "Male" },
    { name: "Female", val: "Female" },
    { name: "Other", val: "Other" },
  ];

  return (
    <>
      <Head>
        <title>
          Astrology Meetup at Koi Fish Cafe, Da Nang – September 18 | School of
          Our Divine Infinite Being
        </title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta
          name="description"
          content="Free 3-hour Astrology Meetup at Koi Fish Cafe, Da Nang on Friday, September 18, 6–9 PM. Explore how the planets shape your identity and destiny with Eddie Paredes. Limited to 30 students — register now."
        />
        <meta
          name="keywords"
          content="Astrology Meetup Da Nang, Koi Fish Cafe Da Nang, Astrology Class Vietnam, Natal Chart, Planets Astrology, Eddie Paredes, School of Our Divine Infinite Being, free astrology class Vietnam"
        />
        <meta
          property="og:title"
          content="Unlock the Secrets of the Planets — Astrology Meetup at Koi Fish Cafe, Da Nang"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:description"
          content="Discover how the cosmos shapes your life. A free astrology class on Friday, September 18 at Koi Fish Cafe, Da Nang. Limited to 30 students."
        />
        <meta
          property="og:url"
          content="https://www.divineinfinitebeing.com/KoiFish-Cafe"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Unlock the Secrets of the Planets — Astrology Meetup at Koi Fish Cafe, Da Nang"
        />
        <meta
          name="twitter:description"
          content="Free astrology class in Da Nang on September 18. Explore how the planets shape your identity and destiny. Limited to 30 students — register now."
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.divineinfinitebeing.com/KoiFish-Cafe"
        />
      </Head>

      <div className={styles.divine_signup_page_main}>
        <div className={styles.divine_signup_page_cmmn_wdth}>
          <div className={styles.divine_signup_page_main_flx}>
            {/* ---------------- LEFT: Event content ---------------- */}
            <div className={styles.divine_signup_page_left}>
              <div className={styles.divine_signup_page_left_top_sec}>
                <div className={styles.divine_signup_page_left_sub_left}>
                  <div className={styles.sign_up_logo_wrpr}>
                    <span>
                      <img
                        src="https://all-frontend-assets.s3.amazonaws.com/transcendentpagan/transcendentpagan_ui_next/sign_up/divine_sign_up_logo.webp"
                        alt="School of Our Divine Infinite Being"
                      />
                    </span>
                    <div className={styles.sign_up_logo_txt}>
                      <h1 data-text="SCHOOL OF THE DIVINE">
                        SCHOOL OF THE DIVINE
                      </h1>
                      <h1 data-text="SCHOOL OF THE DIVINE">INFINITE BEING</h1>
                    </div>
                  </div>
                </div>

                <div className={styles.divine_signup_page_right_sub_right}>
                  <div className={styles.koi_hero}>
                    <h4>Astrology Meetup in Da Nang</h4>
                    <span className={styles.koi_hero_divider}></span>
                    <p>
                      Discover How the Cosmos Shapes Your Life —{" "}
                      <span>Class is at no cost.</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.divine_signup_mddl_txt}>
                <h2 className={styles.koi_hero_title}>
                  Unlock the Secrets of the Planets: Astrology for Personal
                  Identity, Destiny &amp; Chart Mastery
                </h2>
                <p className={styles.koi_intro}>
                  Join us for an immersive Astrology Meetup where you&rsquo;ll
                  explore the powerful influence of the planets on your
                  personality, life path, and destiny. Whether you&rsquo;re new
                  to astrology or seeking a deeper refresher, this interactive
                  class will give you practical insights you can apply
                  immediately.
                </p>
              </div>

              <div className={styles.koi_zodiac_band} aria-hidden="true">
                <span>♈</span><span>♉</span><span>♊</span><span>♋</span>
                <span>♌</span><span>♍</span><span>♎</span><span>♏</span>
                <span>♐</span><span>♑</span><span>♒</span><span>♓</span>
              </div>

              <div className={styles.koi_format}>
                <button
                  type="button"
                  className={styles.koi_wheel_wrap}
                  onClick={() => setWheelOpen(true)}
                  aria-label="View the full zodiac wheel"
                >
                  <img
                    className={styles.koi_wheel}
                    src="https://all-frontend-assets.s3.amazonaws.com/transcendentpagan/assets/images/rota-the-wheel/rotathewheelblockthreeimg.webp"
                    alt="Tropical Western Astrology zodiac wheel"
                  />
                  <span className={styles.koi_wheel_zoom} aria-hidden="true">
                    <AutoAwesomeIcon /> View
                  </span>
                </button>
                <div className={styles.koi_format_txt}>
                  <h3 className={styles.koi_sub_head}>About This Class</h3>
                  <div className={styles.koi_badges}>
                    <span className={styles.koi_badge}>
                      <LanguageIcon /> Taught in English
                    </span>
                    <span className={styles.koi_badge}>
                      <AutoAwesomeIcon /> Tropical Western Astrology
                    </span>
                  </div>
                  <p>
                    This session is conducted in <span>English</span> and
                    follows the <span>Tropical Western Astrology</span> system —
                    approachable for newcomers and enriching for seasoned
                    students alike.
                  </p>
                </div>
              </div>

              <div className={styles.koi_event_card}>
                <h3 className={styles.koi_event_head}>Event Details</h3>
                <div className={styles.koi_event_list}>
                  <div className={styles.koi_event_row}>
                    <span className={styles.koi_event_icon}>
                      <EventIcon />
                    </span>
                    <span className={styles.koi_event_text}>
                      Friday, September 18th
                    </span>
                  </div>
                  <div className={styles.koi_event_row}>
                    <span className={styles.koi_event_icon}>
                      <AccessTimeFilledIcon />
                    </span>
                    <span className={styles.koi_event_text}>
                      6:00 PM &ndash; 9:00 PM
                    </span>
                  </div>
                  <div
                    className={`${styles.koi_event_row} ${styles.koi_event_row_wide}`}
                  >
                    <span className={styles.koi_event_icon}>
                      <StorefrontIcon />
                    </span>
                    <span className={styles.koi_event_text}>Koi Fish Cafe</span>
                  </div>
                  <div
                    className={`${styles.koi_event_row} ${styles.koi_event_row_wide}`}
                  >
                    <span className={styles.koi_event_icon}>
                      <PlaceIcon />
                    </span>
                    <span className={styles.koi_event_text}>
                      68-70 Hồ Sỹ Tân, Sơn Trà, Đà Nẵng, Vietnam
                    </span>
                  </div>
                </div>
              </div>

              <p className={styles.koi_limit}>
                Limited to 30 students only — Please register on this page to
                secure your spot.
              </p>

              <div className={styles.koi_block}>
                <h3 className={styles.koi_section_head}>
                  What You&rsquo;ll Experience
                </h3>
                <p>
                  This 3-hour session blends inspiring lecture with hands-on
                  workshop activities. You&rsquo;ll learn:
                </p>
                <div className={styles.divine_signup_last_sec_bullet}>
                  <p>The core meaning and energy of each planet in astrology</p>
                  <p>
                    How planets shape your personal identity and unique
                    character
                  </p>
                  <p>
                    Their role in guiding your destiny, opportunities, and life
                    lessons
                  </p>
                  <p>
                    How to interpret planets in a natal chart with clarity and
                    confidence
                  </p>
                  <p>
                    Practical techniques for using planetary insights in daily
                    life and decision-making
                  </p>
                </div>
                <p style={{ marginTop: "18px" }}>
                  Materials are provided — no prior preparation needed. Bring
                  your curiosity, a notebook if you like, and an open heart.
                </p>
              </div>

              <div className={styles.koi_block}>
                <h3 className={styles.koi_section_head}>
                  Perfect For Both Beginners &amp; Advanced Students
                </h3>
                <p>
                  <span className={styles.koi_sub_head}>Beginners:</span> Build
                  a strong foundational understanding in a supportive,
                  judgment-free space.
                </p>
                <p>
                  <span className={styles.koi_sub_head}>
                    Advanced Students:
                  </span>{" "}
                  Gain fresh perspectives, refine your chart-reading skills, and
                  connect with like-minded astrologers.
                </p>
                <p>
                  Leave with new knowledge, practical tools, and a richer
                  connection to the cosmic forces at work in your life.
                </p>
              </div>

              <div className={styles.koi_guide}>
                <h3 className={styles.koi_section_head}>
                  Your Guide: Eddie Paredes
                </h3>
                <p>
                  Taught by Eddie Paredes of the School of Our Divine Infinite
                  Being. Eddie brings a clear, intuitive, and transformative
                  approach to astrology, blending ancient wisdom with practical
                  modern application. Learn directly from an experienced teacher
                  dedicated to helping you awaken your highest potential.
                </p>
                <p>
                  Website:{" "}
                  <a
                    href="https://www.divineinfinitebeing.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    www.divineinfinitebeing.com
                  </a>
                </p>
              </div>

              <div className={styles.divine_signup_last_sec}>
                <h5>Why Attend?</h5>
                <div className={styles.divine_signup_last_sec_bullet}>
                  <p>
                    Deepen self-understanding and clarity about your life path
                  </p>
                  <p>Connect with a conscious community in Da Nang</p>
                  <p>Enjoy a beautiful wellness space with cafe vibes</p>
                  <p>Take home lasting insights and astrological tools</p>
                </div>
                <p
                  style={{
                    color: "#fff",
                    fontSize: "22px",
                    lineHeight: "30px",
                    fontWeight: 600,
                    marginTop: "20px",
                  }}
                >
                  Space is strictly limited to 30 students. The class is free.
                  Please register on this page to reserve your seat.
                </p>
              </div>
            </div>

            {/* ---------------- RIGHT: Registration form ---------------- */}
            <div className={styles.divine_signup_page_right}>
              <div className={styles.divine_signup_page_right_inn}>
                <div className={styles.divine_signup_page_right_form_hdr}>
                  <h2>Reserve Your Seat</h2>
                </div>

                <form>
                  <TextField
                    className={styles.from_input}
                    name="first_name"
                    value={user?.first_name || ""}
                    onChange={handleInput}
                    error={error.first_name ? true : false}
                    helperText={error.first_name}
                    variant="outlined"
                    label="First Name"
                  />

                  <TextField
                    className={styles.from_input}
                    name="last_name"
                    value={user?.last_name || ""}
                    onChange={handleInput}
                    error={error.last_name ? true : false}
                    helperText={error.last_name}
                    variant="outlined"
                    label="Last Name"
                  />

                  <TextField
                    className={styles.from_input}
                    name="email"
                    value={user?.email || ""}
                    onChange={handleInput}
                    error={error.email ? true : false}
                    helperText={error.email}
                    variant="outlined"
                    label="Email"
                  />

                  <TextField
                    className={styles.from_input}
                    variant="outlined"
                    fullWidth
                    name="contact_number"
                    type="text"
                    label="Telephone Number"
                    onChange={phoneFormatting}
                    value={user?.contact_number || ""}
                    error={error.contact_number ? true : false}
                    helperText={error.contact_number}
                  />

                  <FormControl className={styles.from_input}>
                    <InputLabel id="danang-label">
                      Do you live in Da Nang?
                    </InputLabel>
                    <Select
                      className="contact_select_menu"
                      labelId="danang-label"
                      label="Do you live in Da Nang?"
                      name="lives_in_danang"
                      onChange={handleInput}
                      value={user?.lives_in_danang || ""}
                      error={error.lives_in_danang ? true : false}
                    >
                      {YesNoArray.map((item, key) => (
                        <MenuItem
                          className="contact_menu_item"
                          value={item.val}
                          key={key}
                        >
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {error.lives_in_danang && (
                      <FormHelperText>{error.lives_in_danang}</FormHelperText>
                    )}
                  </FormControl>

                  <FormControl className={styles.from_input}>
                    <InputLabel id="experience-label">
                      Experience with Astrology
                    </InputLabel>
                    <Select
                      className="contact_select_menu"
                      labelId="experience-label"
                      label="Experience with Astrology"
                      name="experience"
                      onChange={handleInput}
                      value={user?.experience || ""}
                      error={error.experience ? true : false}
                    >
                      {ExperienceArray.map((item, key) => (
                        <MenuItem
                          className="contact_menu_item"
                          value={item.val}
                          key={key}
                        >
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {error.experience && (
                      <FormHelperText>{error.experience}</FormHelperText>
                    )}
                  </FormControl>

                  <TextField
                    className={styles.from_input}
                    name="age"
                    type="number"
                    value={user?.age || ""}
                    onChange={handleInput}
                    error={error.age ? true : false}
                    helperText={error.age}
                    variant="outlined"
                    label="Your Age"
                    inputProps={{ min: 0, max: 120 }}
                  />

                  <FormControl className={styles.from_input}>
                    <InputLabel id="gender-label">Gender</InputLabel>
                    <Select
                      className="contact_select_menu"
                      labelId="gender-label"
                      label="Gender"
                      name="gender"
                      onChange={handleInput}
                      value={user?.gender || ""}
                      error={error.gender ? true : false}
                    >
                      {GenderArray.map((item, key) => (
                        <MenuItem
                          className="contact_menu_item"
                          value={item.val}
                          key={key}
                        >
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {error.gender && (
                      <FormHelperText>{error.gender}</FormHelperText>
                    )}
                  </FormControl>

                  <FormControl className={styles.from_input}>
                    <InputLabel id="believe-label">
                      Do you believe in Astrology?
                    </InputLabel>
                    <Select
                      className="contact_select_menu"
                      labelId="believe-label"
                      label="Do you believe in Astrology?"
                      name="believe_in_astrology"
                      onChange={handleInput}
                      value={user?.believe_in_astrology || ""}
                      error={error.believe_in_astrology ? true : false}
                    >
                      {YesNoArray.map((item, key) => (
                        <MenuItem
                          className="contact_menu_item"
                          value={item.val}
                          key={key}
                        >
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {error.believe_in_astrology && (
                      <FormHelperText>
                        {error.believe_in_astrology}
                      </FormHelperText>
                    )}
                  </FormControl>

                  {submitError && (
                    <p className={styles.koi_submit_error} role="alert">
                      {submitError}
                    </p>
                  )}

                  <Button
                    variant="contained"
                    type="submit"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? "Registering…" : "Register"}
                  </Button>
                </form>
              </div>
            </div>
          </div>

          {/* ---------------- Free-on-YouTube CTA ---------------- */}
          <div className={styles.koi_cta}>
            <h3>
              All of our video course material is available free on YouTube!
            </h3>
            <p>
              What we charge for is the <span>Certification</span>,{" "}
              <span>Live Trainings</span> and the privilege of becoming a{" "}
              <span>Diviner in Astrology and Tarot</span> in our Network.
            </p>
            <div className={styles.koi_cta_btns}>
              <a
                className={`${styles.koi_cta_btn} ${styles.koi_cta_btn_gold}`}
                href="/diviner-academy"
              >
                <SchoolIcon /> Enroll Now
              </a>
              <a
                className={`${styles.koi_cta_btn} ${styles.koi_cta_btn_jade}`}
                href="https://youtube.com/@DivineInfiniteBeing-cg9lo"
                target="_blank"
                rel="noopener noreferrer"
              >
                <YouTubeIcon /> Go to Our YouTube Channel
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ---------------- Success modal ---------------- */}
      <Modal
        open={successOpen}
        onClose={closeSuccess}
        aria-labelledby="koi-success-title"
        className={styles.koi_modal_root}
      >
        <Box className={styles.koi_modal_box}>
          <div className={styles.koi_modal_inner}>
            <span className={styles.koi_modal_close} onClick={closeSuccess}>
              &times;
            </span>

            <div className={styles.koi_modal_glow}>
              <svg
                className={styles.koi_check}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 52 52"
              >
                <circle
                  className={styles.koi_check_circle}
                  cx="26"
                  cy="26"
                  r="25"
                  fill="none"
                />
                <path
                  className={styles.koi_check_mark}
                  fill="none"
                  d="M14.1 27.2l7.1 7.2 16.7-16.8"
                />
              </svg>
              <span className={styles.koi_spark} data-i="1"></span>
              <span className={styles.koi_spark} data-i="2"></span>
              <span className={styles.koi_spark} data-i="3"></span>
              <span className={styles.koi_spark} data-i="4"></span>
              <span className={styles.koi_spark} data-i="5"></span>
              <span className={styles.koi_spark} data-i="6"></span>
            </div>

            <h2 id="koi-success-title" className={styles.koi_modal_title}>
              You&rsquo;re Registered!
            </h2>
            <p className={styles.koi_modal_text}>
              Your seat for the <span>Da Nang Astrology Meetup</span> is
              reserved. We&rsquo;ll see you on{" "}
              <span>Friday, September 18th</span> at Koi Fish Cafe. A
              confirmation will follow by email.
            </p>

            <button
              type="button"
              className={styles.koi_modal_btn}
              onClick={closeSuccess}
            >
              Wonderful!
            </button>
          </div>
        </Box>
      </Modal>

      {/* ---------------- Zodiac wheel lightbox ---------------- */}
      <Modal
        open={wheelOpen}
        onClose={() => setWheelOpen(false)}
        aria-label="Zodiac wheel"
        className={styles.koi_wheel_modal_root}
      >
        <Box className={styles.koi_wheel_modal_box}>
          <span
            className={styles.koi_modal_close}
            onClick={() => setWheelOpen(false)}
          >
            &times;
          </span>
          <img
            className={styles.koi_wheel_modal_img}
            src="https://all-frontend-assets.s3.amazonaws.com/transcendentpagan/assets/images/rota-the-wheel/rotathewheelblockthreeimg.webp"
            alt="Tropical Western Astrology zodiac wheel — full view"
          />
        </Box>
      </Modal>
    </>
  );
}
